import React, { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { 
  User,
  signInWithPopup,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signOut as firebaseSignOut,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { auth, db, googleProvider, SUBSCRIPTION_TIERS, normalizeSubscriptionTier } from '../services/firebase';
import { UserData, SubscriptionTier } from '../types';
import { reportError, setUserContext, clearUserContext } from '../services/errorReporting';

function getEmailLinkErrorMessage(code: string): string {
  if (code === 'auth/invalid-action-code' || code === 'auth/expired-action-code') {
    return 'הקישור פג תוקף או כבר נוצל. אנא בקש לינק חדש מהתחברות.';
  }
  if (code === 'auth/invalid-email') {
    return 'כתובת האימייל אינה תואמת ללינק. הזן את האימייל שקיבל את הקישור.';
  }
  return 'התחברות דרך הקישור נכשלה. אנא בקש לינק חדש ונסה שוב.';
}

function getUserDataErrorMessage(error: unknown): string {
  const code = error && typeof error === 'object' && 'code' in error ? String((error as { code: string }).code) : '';
  if (code === 'permission-denied') {
    return 'אין הרשאה ליצור חשבון. ייתכן שכללי Firestore לא מעודכנים.';
  }
  if (code === 'unavailable' || code === 'resource-exhausted') {
    return 'בעיית רשת. נסה שוב.';
  }
  return 'שגיאה בטעינת/יצירת החשבון. נסה לרענן או לנסות שוב.';
}

interface AuthContextType {
  user: User | null;
  userData: UserData | null;
  loading: boolean;
  userDataError: string | null;
  clearUserDataError: () => void;
  refreshUserData: () => Promise<void>;
  isEmailLinkUrl: boolean;
  emailLinkError: string | null;
  clearEmailLinkError: () => void;
  signInWithGoogle: () => Promise<void>;
  sendSignInLink: (email: string) => Promise<void>;
  completeSignInWithEmailLink: (email?: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper to get next month's reset date
const getNextResetDate = (): Date => {
  const now = new Date();
  const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  return nextMonth;
};

// Create or get user document
const getOrCreateUserDocument = async (user: User): Promise<UserData> => {
  const userRef = doc(db, 'users', user.uid);
  const userSnap = await getDoc(userRef);

  if (userSnap.exists()) {
    const data = userSnap.data();
    return {
      uid: user.uid,
      email: user.email || '',
      displayName: user.displayName || undefined,
      photoURL: user.photoURL || undefined,
      subscription: normalizeSubscriptionTier(data.subscription),
      credits: data.credits,
      creditsResetDate: data.creditsResetDate?.toDate() || getNextResetDate(),
      createdAt: data.createdAt?.toDate() || new Date()
    };
  }

  // Create new user document with free tier
  const newUserData: Omit<UserData, 'uid'> = {
    email: user.email || '',
    displayName: user.displayName || undefined,
    photoURL: user.photoURL || undefined,
    subscription: 'free',
    credits: SUBSCRIPTION_TIERS.free.monthlyCredits,
    creditsResetDate: getNextResetDate(),
    createdAt: new Date()
  };

  await setDoc(userRef, {
    email: newUserData.email,
    displayName: newUserData.displayName ?? null,
    photoURL: newUserData.photoURL ?? null,
    subscription: newUserData.subscription,
    credits: newUserData.credits,
    createdAt: serverTimestamp(),
    creditsResetDate: Timestamp.fromDate(newUserData.creditsResetDate)
  });

  return {
    uid: user.uid,
    ...newUserData
  };
};

interface AuthProviderProps {
  children: ReactNode;
}

const targetOrigin = () => import.meta.env.VITE_FIREBASE_EMAIL_LINK_URL || (typeof window !== 'undefined' ? window.location.origin : '');

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [userDataError, setUserDataError] = useState<string | null>(null);
  const [isEmailLinkUrl, setIsEmailLinkUrl] = useState(false);
  const [emailLinkError, setEmailLinkError] = useState<string | null>(null);
  const emailLinkAttemptedRef = useRef(false);

  const clearUserDataError = () => setUserDataError(null);
  const clearEmailLinkError = () => setEmailLinkError(null);

  const refreshUserData = async () => {
    if (!user) return;
    setUserDataError(null);
    try {
      const data = await getOrCreateUserDocument(user);
      setUserData(data);
      setUserDataError(null);
      setUserContext({ uid: user.uid, subscription: data.subscription });
    } catch (error) {
      reportError(error, { module: 'auth', extra: { action: 'refresh-user-data', uid: user.uid } });
      setUserDataError(getUserDataErrorMessage(error));
    }
  };

  // Email link configuration
  const actionCodeSettings = {
    url: targetOrigin(),
    handleCodeInApp: true
  };

  useEffect(() => {
    const urlIsEmailLink = typeof window !== 'undefined' && isSignInWithEmailLink(auth, window.location.href);

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);

      if (firebaseUser) {
        try {
          const data = await getOrCreateUserDocument(firebaseUser);
          setUserData(data);
          setUserDataError(null);
          setUserContext({ uid: firebaseUser.uid, subscription: data.subscription });
        } catch (error) {
          reportError(error, { module: 'auth', extra: { action: 'fetch-user-data', uid: firebaseUser.uid } });
          setUserData(null);
          setUserDataError(getUserDataErrorMessage(error));
        }
      } else {
        setUserData(null);
        setUserDataError(null);
        clearUserContext();
      }

      if (firebaseUser || emailLinkAttemptedRef.current || !urlIsEmailLink) {
        setLoading(false);
      }
    });

    const clearUrl = () => {
      if (typeof window !== 'undefined') {
        window.history.replaceState({}, document.title, targetOrigin());
      }
    };

    const finishEmailLinkSignIn = async () => {
      try {
        if (typeof window === 'undefined' || !isSignInWithEmailLink(auth, window.location.href)) return;

        let email = window.localStorage.getItem('clicktube_email_for_signin') || undefined;

        if (!email) {
          setIsEmailLinkUrl(true);
          return;
        }

        await signInWithEmailLink(auth, email, window.location.href);
        window.localStorage.removeItem('clicktube_email_for_signin');
        setEmailLinkError(null);
        clearUrl();
      } catch (error: unknown) {
        reportError(error, { module: 'auth', extra: { action: 'email-link-sign-in' } });
        const code = error && typeof error === 'object' && 'code' in error ? String((error as { code: string }).code) : '';
        setEmailLinkError(getEmailLinkErrorMessage(code));
        clearUrl();
      } finally {
        emailLinkAttemptedRef.current = true;
      }
    };

    (async () => {
      if (urlIsEmailLink) {
        await finishEmailLinkSignIn();
        setLoading(false);
      }
    })();

    return () => unsubscribe();
  }, []);

  const signInWithGoogle = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      reportError(error, { module: 'auth', extra: { action: 'google-sign-in' } });
      throw error;
    }
  };

  const sendSignInLink = async (email: string) => {
    try {
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      window.localStorage.setItem('clicktube_email_for_signin', email);
    } catch (error) {
      reportError(error, { module: 'auth', extra: { action: 'send-email-link' } });
      throw error;
    }
  };

  const completeSignInWithEmailLink = async (email?: string) => {
    setEmailLinkError(null);
    try {
      const currentUrl = window.location.href;
      if (!isSignInWithEmailLink(auth, currentUrl)) return;

      const resolvedEmail = email || window.localStorage.getItem('clicktube_email_for_signin') || undefined;

      if (!resolvedEmail) return;

      await signInWithEmailLink(auth, resolvedEmail, currentUrl);
      window.localStorage.removeItem('clicktube_email_for_signin');
      setIsEmailLinkUrl(false);
      setEmailLinkError(null);
      window.history.replaceState({}, document.title, targetOrigin());
    } catch (error: unknown) {
      reportError(error, { module: 'auth', extra: { action: 'complete-email-link-manual' } });
      const code = error && typeof error === 'object' && 'code' in error ? String((error as { code: string }).code) : '';
      setEmailLinkError(getEmailLinkErrorMessage(code));
      window.history.replaceState({}, document.title, targetOrigin());
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
    } catch (error) {
      reportError(error, { module: 'auth', extra: { action: 'sign-out' } });
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    userData,
    loading,
    userDataError,
    clearUserDataError,
    refreshUserData,
    isEmailLinkUrl,
    emailLinkError,
    clearEmailLinkError,
    signInWithGoogle,
    sendSignInLink,
    completeSignInWithEmailLink,
    signOut
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
